import React from 'react';
import { FiAlertTriangle, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import IconWrapper from '../../ui/IconWrapper';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import type {
  FOSFailedDecisionsResponse,
  FOSStuckDecisionsResponse,
} from '../../../hooks/useFOSAdmin';
import { formatRelativeTime } from './fosAdminDashboardUtils';

interface FOSAdminIssuesCardProps {
  issuesCount: number;
  stuckCount: number;
  failedCount: number;
  stuckDecisions?: FOSStuckDecisionsResponse;
  failedDecisions?: FOSFailedDecisionsResponse;
  requeueStuckPending: boolean;
  reprocessAllPending: boolean;
  resumeDecisionPending: boolean;
  reprocessSinglePending: boolean;
  onRequeueStuck: () => void;
  onReprocessAllFailed: () => void;
  onResumeDecision: (decisionId: string) => void;
  onProcessSingle: (decisionId: string) => void;
}

const FOSAdminIssuesCard: React.FC<FOSAdminIssuesCardProps> = ({
  issuesCount,
  stuckCount,
  failedCount,
  stuckDecisions,
  failedDecisions,
  requeueStuckPending,
  reprocessAllPending,
  resumeDecisionPending,
  reprocessSinglePending,
  onRequeueStuck,
  onReprocessAllFailed,
  onResumeDecision,
  onProcessSingle,
}) => {
  if (issuesCount <= 0) {
    return null;
  }

  return (
    <Card className="border-red-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-full">
              <IconWrapper icon={FiAlertTriangle} className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <CardTitle className="text-base">Decisions Needing Attention</CardTitle>
              <CardDescription className="text-xs">
                {issuesCount} decision{issuesCount !== 1 ? 's' : ''} with processing issues
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            {stuckCount > 0 && (
              <Button
                onClick={onRequeueStuck}
                disabled={requeueStuckPending}
                variant="outline"
                size="sm"
              >
                <IconWrapper icon={FiRefreshCw} className={`h-4 w-4 mr-1 ${requeueStuckPending ? 'animate-spin' : ''}`} />
                Resume Stuck
              </Button>
            )}
            {failedCount > 0 && (
              <Button
                onClick={onReprocessAllFailed}
                disabled={reprocessAllPending}
                variant="outline"
                size="sm"
              >
                <IconWrapper icon={FiRefreshCw} className={`h-4 w-4 mr-1 ${reprocessAllPending ? 'animate-spin' : ''}`} />
                Retry Failed
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Update</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stuckDecisions?.decisions?.map((decision) => (
              <TableRow key={decision.id}>
                <TableCell className="font-medium">
                  {decision.reference ?? decision.id.slice(0, 8)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                    Stuck: {decision.processing_status}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-600">
                  {formatRelativeTime(decision.updated_at)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    onClick={() => onResumeDecision(decision.id)}
                    disabled={resumeDecisionPending}
                    variant="ghost"
                    size="sm"
                  >
                    <IconWrapper icon={FiPlay} className="h-4 w-4 mr-1" />
                    Resume
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {failedDecisions?.decisions?.map((decision) => (
              <TableRow key={decision.id}>
                <TableCell className="font-medium">
                  {decision.reference ?? decision.id.slice(0, 8)}
                </TableCell>
                <TableCell>
                  <Badge variant="destructive" className="text-xs">
                    {decision.processing_status}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-600">
                  {formatRelativeTime(decision.updated_at)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    onClick={() => onProcessSingle(decision.id)}
                    disabled={reprocessSinglePending}
                    variant="ghost"
                    size="sm"
                  >
                    <IconWrapper icon={FiRefreshCw} className="h-4 w-4 mr-1" />
                    Retry
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {(stuckCount > 5 || failedCount > 5) && (
          <p className="mt-3 text-xs text-gray-500">
            Showing first 5 of each type. Use Browse tab for full list.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default FOSAdminIssuesCard;
